<template>
	<div class="chart-canvas" :style="{width: cssSize(width), height: cssSize(height)}">
		<canvas ref="canvas" role="img" :aria-label="options.plugins && options.plugins.title && options.plugins.title.text || 'Diagram'"></canvas>
	</div>
</template>

<script>
import Chart     from 'chart.js/auto/auto.mjs';
import cloneDeep from 'lodash/cloneDeep.js';

// Percentage labels for report pies, including legend visibility changes.
const pieLabels = {
	id: 'pieLabels',
	afterDatasetsDraw(chart, args, options) {
		if(!options.display) {
			return;
		}
		const values = chart.data.datasets[0].data;
		const total = values.reduce((sum, value, index) => sum + (chart.getDataVisibility(index) ? value : 0), 0);
		const {ctx} = chart;
		ctx.save();
		ctx.font = 'bold 12px "Roboto Condensed", sans-serif';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		chart.getDatasetMeta(0).data.forEach((arc, index) => {
			if(!total || !values[index] || !chart.getDataVisibility(index)) {
				return;
			}
			const {x, y} = arc.tooltipPosition();
			const label = `${(values[index] / total * 100).toLocaleString('sv-SE', {maximumFractionDigits: 1})}%`;
			ctx.lineWidth = 3;
			ctx.strokeStyle = '#333';
			ctx.strokeText(label, x, y);
			ctx.fillStyle = '#fff';
			ctx.fillText(label, x, y);
		});
		ctx.restore();
	},
};

export default {
	name:  'ChartCanvas',
	props: {
		type:    {type: String, required: true},
		data:    {type: Object, required: true},
		options: {type: Object, default: () => ({})},
		width:   {type: [String, Number], default: '100%'},
		height:  {type: [String, Number], default: 250},
	},
	watch: {
		'data':                {deep: true, handler: 'renderChart'},
		'options':             {deep: true, handler: 'renderChart'},
		'type':                'renderChart',
		'$vuetify.theme.dark': 'renderChart',
	},
	mounted() {
		// Keep the Chart instance outside Vue 2's observation system.
		this.renderChart();
	},
	beforeDestroy() {
		this._chart?.destroy();
	},
	methods: {
		cssSize(value) {
			return typeof value === 'number' || (/^\d+$/).test(value) ? `${value}px` : value;
		},
		renderChart() {
			if(!this.$refs.canvas) {
				return;
			}
			this._chart?.destroy();
			const dark = this.$vuetify.theme.dark;
			const color = dark ? '#eee' : '#424242';
			const options = cloneDeep(this.options);
			options.responsive = true;
			options.maintainAspectRatio = false;
			options.animation = false;
			options.locale = 'sv-SE';
			options.color = color;
			options.font = {family: '"Roboto Condensed", sans-serif'};
			options.plugins = options.plugins || {};
			// Pie legends identify slices; other legends identify datasets.
			options.plugins.legend = {
				display: ['pie', 'doughnut', 'polarArea'].includes(this.type) || this.data.datasets.length > 1,
				...options.plugins.legend,
				labels:  {color},
			};
			options.plugins.title = {...options.plugins.title, color};
			options.plugins.tooltip = {
				boxPadding:      6,
				padding:         10,
				...options.plugins.tooltip,
				backgroundColor: dark ? '#424242' : '#fff',
				titleColor:      color,
				bodyColor:       color,
				borderColor:     dark ? '#777' : '#ddd',
				borderWidth:     1,
			};
			for(const scale of Object.values(options.scales || {})) {
				scale.ticks = {...scale.ticks, color};
				scale.grid = {color: dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'};
			}
			this._chart = new Chart(this.$refs.canvas, {
				type:    this.type,
				data:    cloneDeep(this.data),
				options,
				plugins: [pieLabels],
			});
		},
		async dataURI() {
			await this.$nextTick();
			this._chart.resize();
			this._chart.update('none');
			
			return {imgURI: this._chart.toBase64Image()};
		},
	},
};
</script>

<style scoped>
.chart-canvas {
	position: relative;
	min-width: 0;
	max-width: 100%;
}
</style>
